import React, { useState } from 'react';
import { XMarkIcon, DocumentArrowUpIcon, LockClosedIcon, PencilIcon } from '@heroicons/react/24/outline';
import FileUpload from './FileUpload';
import ErrorDisplay from './ErrorDisplay';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import toast from 'react-hot-toast';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'deck' | 'quiz' | 'match';
  onSubmit: (formData: FormData) => Promise<any>;
  subscription?: any;
  onUpgradePrompt?: () => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  type,
  onSubmit,
  subscription,
  onUpgradePrompt
}) => {
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subStep, setSubStep] = useState(1); // Track sub-steps (1-4 for quiz, 1-3 for others)

  // Question types state (only for quiz) - selected types
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState({
    multipleChoice: true,
    trueFalse: false,
    fillBlank: false,
    essay: false
  });

  // Essay configuration
  const [essayMinWords, setEssayMinWords] = useState(30);

  // Get type-specific configurations
  const getTypeConfig = () => {
    switch (type) {
      case 'deck':
        return {
          title: 'Criar Deck a partir de Arquivo',
          description: 'Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará flashcards automaticamente',
          countLabel: 'Quantidade de Cards',
          countOptions: [5, 10, 20, 30, 50, 100, 500],
          freeLimit: 10,
          premiumMax: 500,
          difficultyOptions: [
            { value: 'beginner', label: 'Iniciante' },
            { value: 'intermediate', label: 'Intermediário' },
            { value: 'advanced', label: 'Avançado' }
          ],
          buttonText: 'Gerar Deck do Arquivo',
          loadingText: 'Gerando Deck...',
          processingText: 'Extraindo texto e criando cards...',
          estimatedTime: '~30-90s',
          color: 'purple'
        };
      case 'quiz':
        return {
          title: 'Criar Quiz a partir de Arquivo',
          description: 'Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará questões automaticamente',
          countLabel: 'Quantidade de Questões',
          countOptions: [5, 10, 15, 20, 30, 50],
          freeLimit: 15,
          premiumMax: 50,
          difficultyOptions: [
            { value: 'beginner', label: 'Iniciante' },
            { value: 'intermediate', label: 'Intermediário' },
            { value: 'advanced', label: 'Avançado' }
          ],
          buttonText: 'Gerar Quiz do Arquivo',
          loadingText: 'Gerando Quiz...',
          processingText: 'Extraindo texto e criando questões...',
          estimatedTime: '~30-60s',
          color: 'blue'
        };
      case 'match':
        return {
          title: 'Criar Jogo Match a partir de Arquivo',
          description: 'Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará pares automaticamente',
          countLabel: 'Quantidade de Pares',
          countOptions: [4, 6, 8, 10, 12, 15, 20],
          freeLimit: 8,
          premiumMax: 20,
          difficultyOptions: [
            { value: 'easy', label: 'Fácil' },
            { value: 'medium', label: 'Médio' },
            { value: 'hard', label: 'Difícil' }
          ],
          buttonText: 'Gerar Match do Arquivo',
          loadingText: 'Gerando Match...',
          processingText: 'Extraindo texto e criando pares...',
          estimatedTime: '~20-45s',
          color: 'green'
        };
    }
  };

  const config = getTypeConfig();
  const defaultCount = subscription?.subscriptionTier === 'free' ? Math.min(config.freeLimit, config.countOptions[2]) : config.countOptions[2];

  const [form, setForm] = useState({
    count: defaultCount,
    difficulty: config.difficultyOptions[1].value,
    language: 'portuguese' as 'portuguese' | 'english' | 'spanish',
    additionalInfo: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      toast.error('Por favor, selecione um arquivo');
      return;
    }

    // Validate question types for quiz
    if (type === 'quiz') {
      const hasSelectedType = selectedQuestionTypes.multipleChoice || selectedQuestionTypes.trueFalse || 
                             selectedQuestionTypes.fillBlank || selectedQuestionTypes.essay;
      if (!hasSelectedType) {
        toast.error('Selecione pelo menos um tipo de questão');
        return;
      }
    }

    setIsSubmitting(true);
    setFileError(null);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      
      // Add type-specific field names
      if (type === 'deck') {
        formData.append('cardCount', form.count.toString());
      } else if (type === 'quiz') {
        formData.append('questionCount', form.count.toString());
        
        // Distribute questions among selected types
        const selectedTypes = Object.entries(selectedQuestionTypes).filter(([_, selected]) => selected);
        const baseCount = Math.floor(form.count / selectedTypes.length);
        const remainder = form.count % selectedTypes.length;
        
        const distributedTypes = {
          multipleChoice: selectedQuestionTypes.multipleChoice ? baseCount + (remainder > 0 ? 1 : 0) : 0,
          trueFalse: selectedQuestionTypes.trueFalse ? baseCount + (remainder > 1 ? 1 : 0) : 0,
          fillBlank: selectedQuestionTypes.fillBlank ? baseCount + (remainder > 2 ? 1 : 0) : 0,
          essay: selectedQuestionTypes.essay ? baseCount : 0
        };
        
        // Send questionTypes as separate fields with bracket notation for nested object
        formData.append('questionTypes[multipleChoice]', distributedTypes.multipleChoice.toString());
        formData.append('questionTypes[trueFalse]', distributedTypes.trueFalse.toString());
        formData.append('questionTypes[fillBlank]', distributedTypes.fillBlank.toString());
        formData.append('questionTypes[essay]', distributedTypes.essay.toString());
        
        // Add essayMinWords if essay questions are selected
        if (selectedQuestionTypes.essay) {
          formData.append('essayMinWords', essayMinWords.toString());
        }
      } else if (type === 'match') {
        formData.append('pairsCount', form.count.toString());
      }
      
      formData.append('difficulty', form.difficulty);
      formData.append('language', form.language);
      
      if (form.additionalInfo) {
        formData.append('additionalInfo', form.additionalInfo);
      }

      const result = await onSubmit(formData);
      
      toast.success(result.message || `${type === 'deck' ? 'Deck' : type === 'quiz' ? 'Quiz' : 'Match'} criado com sucesso!`);
      
      // Reset and close
      handleClose();
      
      // Optionally redirect to the created item
      if (result.deckId || result.quizId || result.matchGameId) {
        const id = result.deckId || result.quizId || result.matchGameId;
        setTimeout(() => {
          const path = type === 'deck' ? `/decks/management/${id}` : 
                       type === 'quiz' ? `/quizzes/management/${id}` : 
                       `/matches`;
          window.location.href = path;
        }, 1000);
      }
    } catch (error: any) {
      console.error(`Error creating ${type} from file:`, error);
      
      // Store the full error object for ErrorDisplay component
      setFileError(error);

      // Handle subscription limits
      if (error.response?.data?.code === 'AI_CARD_LIMIT_REACHED' || 
          error.response?.data?.upgradeRequired) {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setFileError(null);
    setSubStep(1);
    setForm({
      count: defaultCount,
      difficulty: config.difficultyOptions[1].value,
      language: 'portuguese',
      additionalInfo: ''
    });
    setSelectedQuestionTypes({
      multipleChoice: true,
      trueFalse: false,
      fillBlank: false,
      essay: false
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-3 pt-4 pb-3 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
          
          {/* Header */}
          <div className="flex items-start justify-between mb-4 sm:mb-6">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {config.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {config.description}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 disabled:opacity-50"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                {(type === 'quiz' ? [1, 2, 3, 4] : [1, 2, 3]).map((stepNum) => (
                  <React.Fragment key={stepNum}>
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                          subStep > stepNum
                            ? 'bg-green-500 text-white'
                            : subStep === stepNum
                            ? `bg-${config.color}-600 text-white ring-4 ring-${config.color}-200 dark:ring-${config.color}-800`
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {subStep > stepNum ? '✓' : stepNum}
                      </div>
                      <span className={`text-xs mt-1 text-center hidden sm:block ${
                        subStep >= stepNum ? `text-${config.color}-600 dark:text-${config.color}-400 font-medium` : 'text-gray-400'
                      }`}>
                        {stepNum === 1 ? 'Arquivo' : 
                         stepNum === 2 ? 'Configuração' : 
                         stepNum === 3 && type === 'quiz' ? 'Tipos' :
                         stepNum === 3 ? 'Revisão' :
                         'Revisão'}
                      </span>
                    </div>
                    {stepNum < (type === 'quiz' ? 4 : 3) && (
                      <div className={`h-1 flex-1 mx-1 rounded transition-all duration-300 ${
                        subStep > stepNum ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="sm:hidden text-center">
                <span className={`text-xs text-${config.color}-600 dark:text-${config.color}-400 font-medium`}>
                  {subStep === 1 ? 'Arquivo' : 
                   subStep === 2 ? 'Configuração' : 
                   subStep === 3 && type === 'quiz' ? 'Tipos de Questões' :
                   subStep === 3 ? 'Revisão' :
                   'Revisão'}
                </span>
              </div>
            </div>

            {/* Error Display */}
            {fileError && (
              <ErrorDisplay
                error={fileError}
                onRetry={() => setFileError(null)}
                onUpgrade={onUpgradePrompt}
                compact={false}
              />
            )}

            {/* Step 1: File Upload */}
            {subStep === 1 && (
              <div className="space-y-4 animate-fadeIn">
                <div className="text-center mb-6">
                  <DocumentArrowUpIcon className={`h-12 w-12 text-${config.color}-500 mx-auto mb-3`} />
                  <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Selecione seu arquivo
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {config.description}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                    Selecione até 5 Arquivos *
                  </label>
                  <FileUpload
                    onFilesChange={(files) => {
                      setSelectedFiles(files);
                      setFileError(null);
                    }}
                    selectedFiles={selectedFiles}
                    error={null}
                    disabled={isSubmitting}
                    acceptedTypes={['.pdf', '.txt', '.doc', '.docx']}
                    maxSizeMB={10}
                  />
                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                    📄 <strong>Tipos aceitos:</strong> PDF, TXT, DOC, DOCX<br />
                    📊 <strong>Tamanho máximo:</strong> 10MB por arquivo (até 5 arquivos)
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Configuration */}
            {subStep === 2 && (
              <div className="space-y-5 animate-fadeIn">
                <div className="text-center mb-6">
                  <svg className={`h-12 w-12 text-${config.color}-500 mx-auto mb-3`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Configure seu {type === 'deck' ? 'deck' : type === 'quiz' ? 'quiz' : 'jogo'}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Personalize quantidade, dificuldade e idioma
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                    {config.countLabel}
                  </label>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    {config.countOptions.map((count) => {
                      const isLocked = count > config.freeLimit && subscription?.subscriptionTier === 'free';
                      const isSelected = form.count === count;
                      return (
                        <button
                          key={count}
                          type="button"
                          disabled={isLocked}
                          onClick={() => {
                            if (!isLocked) {
                              setForm({ ...form, count });
                            } else if (onUpgradePrompt) {
                              onUpgradePrompt();
                            }
                          }}
                          className={`relative px-3 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                            isSelected
                              ? `border-${config.color}-500 bg-${config.color}-50 dark:bg-${config.color}-900/30 text-${config.color}-700 dark:text-${config.color}-300 shadow-md scale-105`
                              : isLocked
                              ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                              : `border-gray-300 dark:border-gray-600 hover:border-${config.color}-400 dark:hover:border-${config.color}-500 text-gray-700 dark:text-gray-300`
                          }`}
                        >
                          {count}
                          {isLocked && (
                            <LockClosedIcon className="h-3 w-3 absolute top-1 right-1 text-amber-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {subscription?.subscriptionTier === 'free' && !isAndroidWebView && (
                    <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg flex items-start">
                      <LockClosedIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        Mais de {config.freeLimit} {type === 'deck' ? 'cards' : type === 'quiz' ? 'questões' : 'pares'} disponível apenas para Premium.{' '}
                        <a 
                          href="https://upmindy.com/pricing" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline font-semibold hover:text-amber-700 dark:hover:text-amber-300"
                        >
                          Ver planos
                        </a>
                      </span>
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Nível de Dificuldade
                    </label>
                    <div className="space-y-2">
                      {config.difficultyOptions.map((diff) => (
                        <button
                          key={diff.value}
                          type="button"
                          onClick={() => setForm({ ...form, difficulty: diff.value })}
                          className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                            form.difficulty === diff.value
                              ? `border-${config.color}-500 bg-${config.color}-50 dark:bg-${config.color}-900/30 text-${config.color}-700 dark:text-${config.color}-300`
                              : `border-gray-300 dark:border-gray-600 hover:border-${config.color}-400 text-gray-700 dark:text-gray-300`
                          }`}
                        >
                          <span className="text-xl mr-3">
                            {diff.value === 'beginner' || diff.value === 'easy' ? '🌱' : 
                             diff.value === 'intermediate' || diff.value === 'medium' ? '🌿' : '🌳'}
                          </span>
                          {diff.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Idioma
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'portuguese', label: 'Português', flag: '🇧🇷' },
                        { value: 'english', label: 'English', flag: '🇺🇸' },
                        { value: 'spanish', label: 'Español', flag: '🇪🇸' }
                      ].map((lang) => (
                        <button
                          key={lang.value}
                          type="button"
                          onClick={() => setForm({ ...form, language: lang.value as any })}
                          className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                            form.language === lang.value
                              ? `border-${config.color}-500 bg-${config.color}-50 dark:bg-${config.color}-900/30 text-${config.color}-700 dark:text-${config.color}-300`
                              : `border-gray-300 dark:border-gray-600 hover:border-${config.color}-400 text-gray-700 dark:text-gray-300`
                          }`}
                        >
                          <span className="text-xl mr-3">{lang.flag}</span>
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Informações Adicionais (Opcional)
                  </label>
                  <textarea
                    id="additionalInfo"
                    rows={3}
                    value={form.additionalInfo}
                    onChange={(e) => setForm({ ...form, additionalInfo: e.target.value })}
                    className={`block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-${config.color}-500 focus:border-${config.color}-500 resize-none text-sm transition-all`}
                    placeholder="Contexto adicional, foco específico, etc."
                  />
                </div>
              </div>
            )}

            {/* Step 3: Question Types (Quiz only) */}
            {type === 'quiz' && subStep === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Tipos de Questões
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Selecione os formatos que deseja no quiz
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {/* Multiple Choice */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.multipleChoice
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-xl shadow-blue-500/30 dark:shadow-blue-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.multipleChoice
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-blue-50 dark:bg-blue-900/20'
                        }`}>
                          📝
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.multipleChoice
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                          }`}>
                            Múltipla Escolha
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.multipleChoice
                              ? 'text-blue-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Questões com 4 opções de resposta
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.multipleChoice}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, multipleChoice: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* True/False */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.trueFalse
                          ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-xl shadow-green-500/30 dark:shadow-green-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.trueFalse
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-green-50 dark:bg-green-900/20'
                        }`}>
                          ✓✗
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.trueFalse
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400'
                          }`}>
                            Verdadeiro ou Falso
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.trueFalse
                              ? 'text-green-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Questões de verdadeiro ou falso
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.trueFalse}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, trueFalse: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* Fill in the Blank */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.fillBlank
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl shadow-amber-500/30 dark:shadow-amber-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold transition-all ${
                          selectedQuestionTypes.fillBlank
                            ? 'bg-white/20 backdrop-blur-sm text-white'
                            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                        }`}>
                          ___
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.fillBlank
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-amber-600 dark:group-hover:text-amber-400'
                          }`}>
                            Preencher Lacuna
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.fillBlank
                              ? 'text-amber-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Completar frases com palavras faltando
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.fillBlank}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, fillBlank: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* Essay */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.essay
                          ? 'bg-gradient-to-br from-purple-500 to-violet-600 shadow-xl shadow-purple-500/30 dark:shadow-purple-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.essay
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-purple-50 dark:bg-purple-900/20'
                        }`}>
                          📄
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.essay
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400'
                          }`}>
                            Dissertativa
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.essay
                              ? 'text-purple-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Respostas abertas avaliadas por IA
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.essay}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, essay: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
            )}

            {/* Step 3/4: Review */}
            {subStep === (type === 'quiz' ? 4 : 3) && (
              <div className="space-y-4 animate-fadeIn">
                <div className="text-center mb-6">
                  <div className={`h-12 w-12 bg-gradient-to-br from-${config.color}-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    Tudo pronto!
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Revise as informações antes de gerar
                  </p>
                </div>

                <div className={`bg-gradient-to-br from-gray-50 to-${config.color}-50 dark:from-gray-800 dark:to-${config.color}-900/20 rounded-xl p-5 space-y-4 border-2 border-gray-200 dark:border-gray-700`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Arquivos</h5>
                      <p className={`text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center`}>
                        <DocumentArrowUpIcon className={`h-5 w-5 mr-2 text-${config.color}-600`} />
                        {selectedFiles.length ? selectedFiles.map((f) => f.name).join(', ') : 'Nenhum arquivo selecionado'}
                      </p>
                      {selectedFiles.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSubStep(1)}
                      className={`text-${config.color}-600 dark:text-${config.color}-400 hover:text-${config.color}-700 p-1`}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                        {config.countLabel.replace('Quantidade de ', '')}
                      </h5>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {form.count} {type === 'deck' ? 'cards' : type === 'quiz' ? 'questões' : 'pares'}
                      </p>
                    </div>
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Dificuldade</h5>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {form.difficulty === 'beginner' || form.difficulty === 'easy' ? '🌱 ' : 
                         form.difficulty === 'intermediate' || form.difficulty === 'medium' ? '🌿 ' : '🌳 '}
                        {config.difficultyOptions.find(d => d.value === form.difficulty)?.label}
                      </p>
                    </div>
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Idioma</h5>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {form.language === 'portuguese' ? '🇧🇷 Português' : form.language === 'spanish' ? '🇪🇸 Español' : '🇺🇸 English'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSubStep(2)}
                      className={`text-xs text-${config.color}-600 dark:text-${config.color}-400 hover:text-${config.color}-700 font-medium text-left flex items-center`}
                    >
                      <PencilIcon className="h-3 w-3 mr-1" />
                      Editar configuração
                    </button>
                  </div>

                  {type === 'quiz' && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tipos de Questões</h5>
                        <button
                          type="button"
                          onClick={() => setSubStep(3)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 p-1"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedQuestionTypes.multipleChoice && (
                          <div className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            📝 Múltipla Escolha
                          </div>
                        )}
                        {selectedQuestionTypes.trueFalse && (
                          <div className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            ✓✗ V ou F
                          </div>
                        )}
                        {selectedQuestionTypes.fillBlank && (
                          <div className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            ___ Lacuna
                          </div>
                        )}
                        {selectedQuestionTypes.essay && (
                          <div className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            📄 Dissertativa
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {form.additionalInfo && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Info Adicional</h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{form.additionalInfo}</p>
                    </div>
                  )}
                </div>

                {!isSubmitting && (
                  <div className={`bg-gradient-to-r from-${config.color}-50 to-indigo-50 dark:from-${config.color}-900/20 dark:to-indigo-900/20 border-2 border-${config.color}-200 dark:border-${config.color}-700 rounded-xl p-4`}>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className={`h-5 w-5 text-${config.color}-500`} viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1 text-sm">
                        <p className={`text-${config.color}-700 dark:text-${config.color}-200 font-semibold mb-1`}>
                          💡 Processamento inteligente
                        </p>
                        <p className={`text-${config.color}-600 dark:text-${config.color}-300`}>
                          Nossa IA extrairá o conteúdo mais relevante do arquivo e criará {type === 'deck' ? 'cards' : type === 'quiz' ? 'questões' : 'pares'} automaticamente.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Progress indicator */}
            {isSubmitting && (
              <div className={`bg-gradient-to-r from-${config.color}-50 to-indigo-50 dark:from-${config.color}-900/20 dark:to-indigo-900/20 rounded-xl p-5 border-2 border-${config.color}-200 dark:border-${config.color}-700`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <DocumentArrowUpIcon className={`h-5 w-5 text-${config.color}-600 dark:text-${config.color}-400 animate-pulse`} />
                    <span className={`text-sm font-semibold text-${config.color}-800 dark:text-${config.color}-200`}>
                      {config.processingText}
                    </span>
                  </div>
                  <span className={`text-xs text-${config.color}-600 dark:text-${config.color}-400 font-medium`}>
                    ⏱️ {config.estimatedTime}
                  </span>
                </div>
                
                <div className={`w-full bg-${config.color}-100 dark:bg-${config.color}-800 rounded-full h-2.5 overflow-hidden`}>
                  <div className={`h-2.5 bg-gradient-to-r from-${config.color}-500 to-indigo-500 dark:from-${config.color}-400 dark:to-indigo-400 rounded-full animate-pulse`}></div>
                </div>
                
                <p className={`text-xs text-${config.color}-700 dark:text-${config.color}-300 mt-3 text-center`}>
                  Extraindo texto e criando conteúdo personalizado com IA...
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  if (subStep > 1) {
                    setSubStep(subStep - 1);
                  } else {
                    handleClose();
                  }
                }}
                disabled={isSubmitting}
                className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← {subStep === 1 ? 'Cancelar' : 'Anterior'}
              </button>

              {subStep < (type === 'quiz' ? 4 : 3) ? (
                <button
                  type="button"
                  onClick={() => {
                    if (subStep === 1 && selectedFiles.length === 0) {
                      toast.error('Por favor, selecione um arquivo');
                      return;
                    }
                    if (type === 'quiz' && subStep === 3) {
                      const hasSelectedType = selectedQuestionTypes.multipleChoice || selectedQuestionTypes.trueFalse || 
                                             selectedQuestionTypes.fillBlank || selectedQuestionTypes.essay;
                      if (!hasSelectedType) {
                        toast.error('Selecione pelo menos um tipo de questão');
                        return;
                      }
                    }
                    setSubStep(subStep + 1);
                  }}
                  className={`flex-1 px-5 py-3.5 bg-gradient-to-r from-${config.color}-600 to-${config.color}-700 hover:from-${config.color}-700 hover:to-${config.color}-800 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-${config.color}-500/30`}
                >
                  Continuar →
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || selectedFiles.length === 0}
                  className={`flex-1 px-5 py-3.5 bg-gradient-to-r from-${config.color}-600 to-indigo-600 hover:from-${config.color}-700 hover:to-indigo-700 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-${config.color}-500/30 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>{config.loadingText}</span>
                    </div>
                  ) : (
                    <span className="flex items-center justify-center">
                      <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                      {config.buttonText}
                    </span>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
